import { InfoOutlineIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  CloseButton,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Spinner,
  Text,
  Textarea,
  Tooltip,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { Instant } from "@js-joda/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import DurationTimer from "../../../shared/components/DurationTimer";
import Select from "../../../shared/components/Select";
import useApi from "../../../shared/hooks/useApi";
import { handleErrorWithToast } from "../../../shared/utils/error-handlers";
import {
  AiWeeklyTemplateSuggestionId,
  AuthorizationId,
  PatientId,
} from "../../../shared/schema/schema";
import {
  ApplyEligibility,
  ApplyResponse,
  emitRefreshVisits,
  formatApplyError,
  formatHours,
  GenerateResponse,
} from "../ai-weekly-template-builder.utils";
import AiWeeklyTemplateApplyBar from "./AiWeeklyTemplateApplyBar";
import AiWeeklyTemplateApplyDialog from "./AiWeeklyTemplateApplyDialog";
import AiWeeklyTemplateContextPanel from "./AiWeeklyTemplateContextPanel";
import AiWeeklyTemplateFeedbackBox from "./AiWeeklyTemplateFeedbackBox";
import AiWeeklyTemplateHistoryList from "./AiWeeklyTemplateHistoryList";
import AiWeeklyTemplateResultPanel from "./AiWeeklyTemplateResultPanel";
import AiWeeklyTemplateVersionStrip from "./AiWeeklyTemplateVersionStrip";

const MAX_RULES_LENGTH = 4000;
const INCREMENT_OPTIONS = [5, 10, 15, 30, 60];
const DEFAULT_INCREMENT_MINUTES = 15;

const INCREMENT_HELP =
  "Every shift start, end and length must land on this boundary. A smaller increment can " +
  "squeeze closer to 100% utilization but produces odd times like 09:00-13:05; a larger one " +
  "gives rounder, easier-to-staff shifts and may leave a few minutes unused. 15 minutes suits " +
  "almost every authorization.";

const RULES_PLACEHOLDER = [
  "e.g. no visits on Sundays; minimum visit 2 hours; maximum visit 8 hours;",
  "patient prefers mornings; no split shifts; caregiver unavailable Wednesdays",
].join("\n");

interface Props {
  patientId: PatientId;
  /**
   * Preselects the authorization, skipping the picker. Set it when the caller
   * already identifies one — e.g. a row in the Authorization Utilization
   * report, which is keyed by authorization. The picker stays usable, so the
   * coordinator can still switch to another of the patient's authorizations.
   */
  initialAuthorizationId?: AuthorizationId;
  onClose: () => void;
}

export default function AiWeeklyTemplateBuilderModal(props: Props) {
  const { queries, mutations } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [authorizationId, setAuthorizationId] = React.useState<AuthorizationId | null>(
    props.initialAuthorizationId ?? null
  );
  const [schedulingRules, setSchedulingRules] = React.useState("");
  const [incrementMinutes, setIncrementMinutes] = React.useState(DEFAULT_INCREMENT_MINUTES);
  const [startedAt, setStartedAt] = React.useState<Instant | null>(null);
  const [result, setResult] = React.useState<GenerateResponse | null>(null);
  const [viewingSuggestionId, setViewingSuggestionId] =
    React.useState<AiWeeklyTemplateSuggestionId | null>(null);
  const [showBuilder, setShowBuilder] = React.useState(false);
  const [contextExpanded, setContextExpanded] = React.useState(true);
  const applyDisclosure = useDisclosure();
  /**
   * Keyed by suggestion id rather than held as a bare response: after applying,
   * clicking a different history row must not show this success against the
   * wrong suggestion.
   */
  const [applied, setApplied] = React.useState<{
    suggestionId: AiWeeklyTemplateSuggestionId;
    response: ApplyResponse;
  } | null>(null);
  const [applyError, setApplyError] = React.useState<string | null>(null);

  const authorizationsQuery = useQuery(
    queries.aiWeeklyTemplateBuilder.authorizations(props.patientId)
  );

  const contextQuery = useQuery({
    ...queries.aiWeeklyTemplateBuilder.context(authorizationId ?? (0 as AuthorizationId)),
    enabled: authorizationId !== null,
  });

  const suggestionDetailQuery = useQuery({
    ...queries.aiWeeklyTemplateBuilder.suggestionDetail(
      authorizationId ?? (0 as AuthorizationId),
      viewingSuggestionId ?? (0 as AiWeeklyTemplateSuggestionId)
    ),
    enabled: authorizationId !== null && viewingSuggestionId !== null,
  });

  const generate = useMutation({
    ...mutations.aiWeeklyTemplateBuilder.generate(authorizationId ?? (0 as AuthorizationId)),
    onSuccess: (response) => {
      // Every non-success outcome comes back as HTTP 200 with a status, so the
      // interesting branches are handled here rather than in onError.
      setResult(response);
      // The result is what matters now; the authorization detail is a lot of
      // screen for something the coordinator has already read.
      setContextExpanded(false);
      void queryClient.invalidateQueries({
        queryKey: ["aiWeeklyTemplateBuilder", props.patientId, "authorizations"],
      });
      if (authorizationId !== null) {
        void queryClient.invalidateQueries({
          queryKey: ["aiWeeklyTemplateBuilder", authorizationId, "context"],
        });
      }
    },
    onError: handleErrorWithToast(toast),
  });

  // Which suggestion the panel is showing: a stored one when a history row is
  // open, otherwise whatever was just generated.
  const shownSuggestionId =
    viewingSuggestionId ?? (result === null ? null : result.suggestionId) ?? null;

  const refine = useMutation({
    ...mutations.aiWeeklyTemplateBuilder.refine(
      authorizationId ?? (0 as AuthorizationId),
      shownSuggestionId ?? (0 as AiWeeklyTemplateSuggestionId)
    ),
    onSuccess: (response) => {
      // A revision is a result like any other, so it lands where a generation
      // lands. Clearing the stored view matters: refining from the history opens
      // the new turn rather than leaving the old one on screen.
      setResult(response);
      setViewingSuggestionId(null);
      setContextExpanded(false);
      setShowBuilder(true);
      void queryClient.invalidateQueries({
        queryKey: ["aiWeeklyTemplateBuilder", props.patientId, "authorizations"],
      });
      if (authorizationId !== null) {
        void queryClient.invalidateQueries({
          queryKey: ["aiWeeklyTemplateBuilder", authorizationId, "context"],
        });
      }
    },
    onError: handleErrorWithToast(toast),
  });

  /** Either model call: both hold the modal and both spend a daily slot. */
  const isGenerating = generate.isPending || refine.isPending;

  const apply = useMutation({
    ...mutations.aiWeeklyTemplateBuilder.apply(
      authorizationId ?? (0 as AuthorizationId),
      shownSuggestionId ?? (0 as AiWeeklyTemplateSuggestionId)
    ),
    onSuccess: (response) => {
      setApplied({ suggestionId: response.suggestionId, response });
      setApplyError(null);
      applyDisclosure.onClose();

      // The React half of the screen.
      void queryClient.invalidateQueries({
        queryKey: ["aiWeeklyTemplateBuilder", props.patientId, "existingTemplate"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["aiWeeklyTemplateBuilder", props.patientId, "authorizations"],
      });
      if (authorizationId !== null) {
        void queryClient.invalidateQueries({
          queryKey: ["aiWeeklyTemplateBuilder", authorizationId, "context"],
        });
      }

      // And the AngularJS half: the weekly template table and the patient
      // calendar below this modal are both now stale. `refresh_visits` is the
      // event those components already listen on, and the one the AngularJS
      // master-week flow broadcasts after the same kind of write.
      emitRefreshVisits();
    },
    onError: (error) => {
      // Kept in the dialog rather than a toast: the coordinator can fix a bad
      // caregiver choice and retry without re-reading everything. Nothing was
      // written — the server rolls the whole apply back.
      setApplyError(formatApplyError(error));
    },
  });

  const rateLimit = contextQuery.data?.rateLimit ?? authorizationsQuery.data?.rateLimit ?? null;
  const outOfQuota = rateLimit !== null && rateLimit.remaining <= 0;
  const canGenerate =
    authorizationId !== null && contextQuery.status === "success" && !isGenerating && !outOfQuota;

  // When this authorization has been worked on before, that is the more useful
  // thing to open on: a coordinator can often answer their question from a past
  // attempt without spending ~50s and a slot from the daily quota. The builder
  // is one click away, and is what opens when there is no history to show.
  const hasHistory =
    contextQuery.status === "success" && contextQuery.data.recentSuggestions.length > 0;
  const inBuilder = !hasHistory || showBuilder;

  const handleGenerate = () => {
    setResult(null);
    setViewingSuggestionId(null);
    setStartedAt(Instant.now());
    generate.mutate({
      schedulingRules: schedulingRules.trim() === "" ? null : schedulingRules.trim(),
      incrementMinutes,
      includeTrueUps: true,
    });
  };

  const handleClose = () => {
    if (isGenerating || apply.isPending) {
      return;
    }
    props.onClose();
  };

  /**
   * Built once and used at both `AiWeeklyTemplateResultPanel` call sites.
   *
   * Sharing the value rather than writing the props out twice is deliberate: the
   * documented failure mode in this file is the live and history call sites
   * drifting apart.
   */
  const renderApplyBar = (
    eligibility: ApplyEligibility,
    appliedAt: Instant | null,
    appliedByName: string | null
  ) => (
    <AiWeeklyTemplateApplyBar
      appliedAt={appliedAt}
      appliedByName={appliedByName}
      eligibility={eligibility}
      isApplying={apply.isPending}
      result={
        // Only for the suggestion actually on screen.
        applied !== null && applied.suggestionId === shownSuggestionId ? applied.response : null
      }
      onClickApply={() => {
        setApplyError(null);
        applyDisclosure.onOpen();
      }}
    />
  );

  // Whichever verification the panel is showing is the one the dialog describes.
  const shownVerification =
    viewingSuggestionId !== null
      ? suggestionDetailQuery.data?.verification ?? null
      : result?.verification ?? null;

  // The conversation the shown suggestion belongs to. It comes from the server
  // with the result rather than being accumulated here, so it survives a reload
  // and is identical whether the suggestion was just produced or reopened later.
  const shownThread =
    (viewingSuggestionId !== null ? suggestionDetailQuery.data?.thread : result?.thread) ?? [];
  const remainingFeedbackTurns =
    (viewingSuggestionId !== null
      ? suggestionDetailQuery.data?.remainingFeedbackTurns
      : result?.remainingFeedbackTurns) ?? 0;

  /**
   * Built once for both call sites, like `renderApplyBar` and for the same
   * reason: the live and history panels drifting apart is this file's documented
   * failure mode.
   */
  const renderVersionStrip = () => (
    <AiWeeklyTemplateVersionStrip
      isDisabled={isGenerating || apply.isPending}
      selectedId={shownSuggestionId}
      turns={shownThread}
      onSelect={(turn) => {
        // The just-produced result is not a stored view — selecting it means
        // closing whichever stored one is open.
        setViewingSuggestionId(
          result !== null && result.suggestionId === turn.suggestionId ? null : turn.suggestionId
        );
      }}
    />
  );

  const renderFeedbackBox = () =>
    shownSuggestionId === null ? null : (
      <AiWeeklyTemplateFeedbackBox
        // Reset the draft when a different version is opened: feedback is always
        // about the suggestion on screen.
        key={Number(shownSuggestionId)}
        isDisabled={outOfQuota || apply.isPending}
        isPending={refine.isPending}
        remainingQuota={rateLimit?.remaining ?? null}
        remainingTurns={remainingFeedbackTurns}
        onSubmit={(feedback) => {
          setStartedAt(Instant.now());
          refine.mutate({ feedback });
        }}
      />
    );

  return (
    <Modal
      isCentered
      closeOnOverlayClick={!isGenerating}
      isOpen={true}
      scrollBehavior="inside"
      size="6xl"
      onClose={handleClose}
    >
      <ModalOverlay />
      <ModalContent height="90vh">
        <ModalHeader>
          <Heading size="md">AI weekly template assistant</Heading>
          <Text color="gray.600" fontSize="sm" fontWeight="normal" mt={2}>
            Suggests one repeating week that uses as much of the authorization as the rules allow.
            Nothing is written to the schedule — the suggestion is yours to review and enter, and it
            is kept here so you can look at it again.
          </Text>
        </ModalHeader>
        {!isGenerating && <ModalCloseButton />}
        {isGenerating && <Progress isIndeterminate size="xs" />}

        <ModalBody display="flex" flexDirection="column" gap={4}>
          {/* Nothing else is actionable for the next minute or so, and leaving
              the form on screen invites edits that the running call will not
              pick up. */}
          {isGenerating && (
            <Flex alignItems="center" direction="column" flex="1" gap={2} justifyContent="center">
              <Spinner size="xl" />
              <Text color="gray.700" fontWeight="semibold" pt={2}>
                {refine.isPending
                  ? "Revising and re-verifying the schedule"
                  : "Building and verifying a schedule"}
              </Text>
              <Text color="gray.600" fontSize="sm">
                This usually takes under two minutes.
              </Text>
              {startedAt !== null && (
                <Text color="gray.500" fontSize="sm">
                  <DurationTimer instant={startedAt} /> elapsed
                </Text>
              )}
            </Flex>
          )}

          {!isGenerating && (
            <>
              <FormControl>
                <FormLabel>Authorization</FormLabel>
                <Select
                  allowUnselect={false}
                  isDisabled={isGenerating}
                  isLoading={authorizationsQuery.isPending}
                  label="Select an authorization"
                  multiple={false}
                  options={(authorizationsQuery.data?.authorizations ?? []).map(
                    (authorization) => ({
                      value: authorization.authorizationId,
                      label: [
                        authorization.isCurrentlyActive ? "● " : "",
                        authorization.contractTypeName,
                        " · ",
                        authorization.serviceCode,
                        " · ",
                        authorization.periodType,
                        " · ",
                        authorization.startDate,
                        " → ",
                        authorization.endDate,
                        authorization.code === null ? "" : ` · ${authorization.code}`,
                        authorization.hasWeeklyTemplate ? " · has template" : "",
                      ].join(""),
                    })
                  )}
                  value={authorizationId}
                  onChange={(value) => {
                    setAuthorizationId(value ?? null);
                    setResult(null);
                    setViewingSuggestionId(null);
                    setShowBuilder(false);
                    setContextExpanded(true);
                  }}
                />
              </FormControl>

              {authorizationsQuery.status === "error" && (
                <Alert status="error">
                  <AlertIcon />
                  <AlertDescription>
                    Could not load this patient&apos;s authorizations.
                  </AlertDescription>
                </Alert>
              )}

              {rateLimit !== null && (
                <Text color={outOfQuota ? "orange.600" : "gray.500"} fontSize="sm">
                  {rateLimit.remaining} of {rateLimit.limit} generations left for this patient today
                  {outOfQuota && rateLimit.nextAvailableAt !== null
                    ? ` — next available at ${rateLimit.nextAvailableAt}`
                    : ""}
                </Text>
              )}

              {authorizationId !== null && contextQuery.status === "pending" && (
                <Flex justifyContent="center" py={6}>
                  <Spinner />
                </Flex>
              )}

              {contextQuery.status === "error" && (
                <Alert status="error">
                  <AlertIcon />
                  <AlertDescription>Could not load this authorization.</AlertDescription>
                </Alert>
              )}

              {/* History first when there is any: usually the fastest way to an
              answer, and it costs nothing. The builder is one click away. */}
              {contextQuery.status === "success" && !inBuilder && (
                <AiWeeklyTemplateHistoryList
                  selectedId={viewingSuggestionId}
                  suggestions={contextQuery.data.recentSuggestions}
                  // Clicking the open row closes it, so the row is the toggle and
                  // the X is just the obvious second way out.
                  onSelect={(suggestion) =>
                    setViewingSuggestionId((current) =>
                      current === suggestion.id ? null : suggestion.id
                    )
                  }
                />
              )}

              {contextQuery.status === "success" && inBuilder && (
                <>
                  <AiWeeklyTemplateContextPanel
                    context={contextQuery.data}
                    isExpanded={contextExpanded}
                    onToggleExpanded={() => setContextExpanded((current) => !current)}
                  />

                  {/* The inputs collapse with the authorization detail: together
                  they are the "set this up" half of the modal, and once a
                  result exists none of it is what the coordinator is reading. */}
                  {contextExpanded && (
                    <>
                      <Divider />

                      <FormControl>
                        <FormLabel>Scheduling rules / restrictions (optional)</FormLabel>
                        <Textarea
                          maxLength={MAX_RULES_LENGTH}
                          placeholder={RULES_PLACEHOLDER}
                          rows={4}
                          value={schedulingRules}
                          onChange={(event) => setSchedulingRules(event.target.value)}
                        />
                        <Text color="gray.500" fontSize="xs" pt={1}>
                          {schedulingRules.length} / {MAX_RULES_LENGTH}
                        </Text>
                      </FormControl>

                      <FormControl maxW="260px">
                        <FormLabel alignItems="center" display="flex" gap={1}>
                          Scheduling increment
                          <Tooltip hasArrow label={INCREMENT_HELP} placement="top">
                            <InfoOutlineIcon aria-label={INCREMENT_HELP} color="gray.400" />
                          </Tooltip>
                        </FormLabel>
                        <Select
                          allowUnselect={false}
                          label="Increment"
                          multiple={false}
                          options={INCREMENT_OPTIONS.map((option) => ({
                            value: option,
                            label: `${option} minutes (${formatHours(option)})`,
                          }))}
                          value={incrementMinutes}
                          onChange={(value) =>
                            setIncrementMinutes(value ?? DEFAULT_INCREMENT_MINUTES)
                          }
                        />
                      </FormControl>
                    </>
                  )}
                </>
              )}

              {/* Sibling of both views: a stored suggestion is opened from the
                  history list, so this must render there too, not only in the
                  builder. */}
              {viewingSuggestionId !== null && (
                <>
                  <Divider />
                  <Flex alignItems="center" justifyContent="space-between">
                    <Text fontWeight="semibold">Viewing a previous suggestion</Text>
                    <CloseButton
                      aria-label="Close this suggestion"
                      size="sm"
                      onClick={() => setViewingSuggestionId(null)}
                    />
                  </Flex>
                  {suggestionDetailQuery.status === "pending" && (
                    <Flex justifyContent="center" py={6}>
                      <Spinner />
                    </Flex>
                  )}
                  {suggestionDetailQuery.status === "error" && (
                    <Alert status="error">
                      <AlertIcon />
                      <AlertDescription>Could not load that suggestion.</AlertDescription>
                    </Alert>
                  )}
                  {suggestionDetailQuery.status === "success" && (
                    <AiWeeklyTemplateResultPanel
                      applyBar={renderApplyBar(
                        suggestionDetailQuery.data.apply,
                        suggestionDetailQuery.data.summary.appliedAt,
                        suggestionDetailQuery.data.summary.appliedByName
                      )}
                      feedbackBox={renderFeedbackBox()}
                      status={suggestionDetailQuery.data.summary.status}
                      suggestion={suggestionDetailQuery.data.suggestion}
                      verification={suggestionDetailQuery.data.verification}
                      versionStrip={renderVersionStrip()}
                    />
                  )}
                </>
              )}

              {inBuilder && viewingSuggestionId === null && result !== null && (
                <>
                  <Divider />
                  <AiWeeklyTemplateResultPanel
                    applyBar={renderApplyBar(result.apply, null, null)}
                    feedbackBox={renderFeedbackBox()}
                    nextAvailableAt={result.context.rateLimit.nextAvailableAt}
                    status={result.status}
                    suggestion={result.suggestion}
                    verification={result.verification}
                    versionStrip={renderVersionStrip()}
                  />
                </>
              )}
            </>
          )}
        </ModalBody>

        <ModalFooter gap={3}>
          <Button isDisabled={isGenerating} variant="ghost" onClick={handleClose}>
            Close
          </Button>

          {inBuilder && hasHistory && (
            <Button
              isDisabled={isGenerating}
              variant="outline"
              onClick={() => {
                setShowBuilder(false);
                setViewingSuggestionId(null);
              }}
            >
              Previous suggestions
            </Button>
          )}

          {inBuilder ? (
            <Button
              colorScheme="blue"
              isDisabled={!canGenerate}
              isLoading={generate.isPending}
              loadingText="Generating…"
              onClick={handleGenerate}
            >
              {result === null ? "Generate" : "Generate again"}
            </Button>
          ) : (
            <Button
              colorScheme="blue"
              isDisabled={contextQuery.status !== "success"}
              onClick={() => {
                setShowBuilder(true);
                setViewingSuggestionId(null);
              }}
            >
              Build a new one
            </Button>
          )}
        </ModalFooter>
      </ModalContent>

      {/*
        A sibling of ModalContent rather than nested inside the body, so it adds
        no depth to this file's already deep conditional tree.
      */}
      {shownSuggestionId !== null &&
        shownVerification !== null &&
        contextQuery.status === "success" && (
          <AiWeeklyTemplateApplyDialog
            authorization={contextQuery.data.authorization}
            errorMessage={applyError}
            isApplying={apply.isPending}
            isOpen={applyDisclosure.isOpen}
            patientName={contextQuery.data.authorization.patientName}
            verification={shownVerification}
            onClose={() => {
              if (apply.isPending) {
                return;
              }
              setApplyError(null);
              applyDisclosure.onClose();
            }}
            onConfirm={({ caregiverId }) =>
              apply.mutate({
                caregiverId,
                // No picker for this: there is no reusable payroll-code select in
                // webapp-react, and the field is nullable end to end. The request
                // type keeps it so adding one later needs no API change.
                payrollCodeId: null,
                // Refuses the write if the snapshot is not the one described on
                // screen.
                expectedWeeklyTotalMinutes: shownVerification.weeklyTotalMinutes,
              })
            }
          />
        )}
    </Modal>
  );
}
